import dayjs from '~/timeInit';

import {
  ScalePricingItem,
  TimelinePoint,
  configurationKey,
  configurationLabel,
  indexPricing,
  isSingleRoom,
  rateAtPax,
} from './departureInventory';

export interface DailyCostPoint {
  time: number;
  balances: Record<string, { cost: number | null; pax: number }>;
}

export interface ConfigurationSeries {
  id: string;
  label: string;
  season: string | null;
}

const pairKey = (tour: string, option: string) => JSON.stringify([tour.toLowerCase(), option.toLowerCase()]);

/** Closing balances per configuration and local day, including an initial zero baseline. */
export function buildDailyCostPoints(
  timeline: Array<TimelinePoint>,
  items: Array<ScalePricingItem>,
  currency: string,
  season: string | null = null,
): { points: Array<DailyCostPoint>; series: Array<ConfigurationSeries> } {
  const movements = timeline.slice().sort((a, b) => dayjs(a.occurredAt).valueOf() - dayjs(b.occurredAt).valueOf());
  const { byConfiguration, seasonUsed } = indexPricing(items, season, currency);
  const inventorySeason = season?.trim() || seasonUsed;
  const configurations = new Map<string, { tour: string; option: string }>();
  Array.from(byConfiguration.values()).forEach((item) =>
    configurations.set(pairKey(item.tourConfiguration, item.packageConfiguration), {
      tour: item.tourConfiguration,
      option: item.packageConfiguration,
    }),
  );
  movements.forEach(({ entry }) =>
    configurations.set(pairKey(entry.tourConfiguration, entry.packageConfiguration), {
      tour: entry.tourConfiguration,
      option: entry.packageConfiguration,
    }),
  );
  const entries = Array.from(configurations.entries()).sort(([a], [b]) => a.localeCompare(b));
  const series = entries.map(([, config], index) => ({
    id: 'configuration' + index,
    label: configurationLabel(config.tour, config.option),
    season: inventorySeason,
  }));
  if (!movements.length) return { points: [], series };

  const counts = new Map<string, { pax: number; singles: number }>();
  const snapshot = (time: number): DailyCostPoint => ({
    time,
    balances: Object.fromEntries(
      entries.map(([key, config], index) => {
        const { pax, singles } = counts.get(key) ?? { pax: 0, singles: 0 };
        const pricing = byConfiguration.get(configurationKey(config.tour, config.option));
        const rate = pricing ? rateAtPax(pricing.scale, pax).unitPrice : null;
        const cost = pax === 0 ? 0 : rate === null ? null : pax * rate + singles * pricing.singleSupplement;
        return [series[index].id, { cost, pax }];
      }),
    ),
  });
  const firstDay = dayjs(movements[0].occurredAt).local().startOf('day');
  const points = [snapshot(firstDay.subtract(1, 'day').valueOf())];
  let day = firstDay.valueOf();
  movements.forEach((point) => {
    const nextDay = dayjs(point.occurredAt).local().startOf('day').valueOf();
    while (day < nextDay) {
      points.push(snapshot(day));
      day = dayjs(day).add(1, 'day').startOf('day').valueOf();
    }
    const key = pairKey(point.entry.tourConfiguration, point.entry.packageConfiguration);
    const previous = counts.get(key) ?? { pax: 0, singles: 0 };
    const change = point.entry.operation === 'allocated' ? 1 : -1;
    counts.set(key, {
      pax: previous.pax + change,
      singles: previous.singles + (isSingleRoom(point.entry.passenger.roomType) ? change : 0),
    });
  });
  points.push(snapshot(day));
  return { points, series };
}
